import {CSSProperties, memo, useEffect, useState} from "react";
import {NodeProps} from "@xyflow/react";
import {HospitalStatus, Metric, TreeNode} from "@/types";
import {BasicCardNode} from "@/pages/mindmap/CarNodes/BasicCardNode.tsx";
import {formatCompactNumber, formatPercentage} from "@/utils/format.ts";
import {fetchHospitals} from "@/services/api";
import {useAppStore} from "@/store/appStore.ts";

// 医院列表卡片组件
export const HospitalListNode = memo(({data}: NodeProps) => {
    const node: TreeNode = data.node as any;
    const batch: string = data.batch as any;
    const selected: boolean = false;

    const cardStyle: CSSProperties = {
        width: 540,
        height: 410,
        padding: '16px 20px',
        background: selected ? '#f8fafc' : 'white',
        borderTop: `1px solid ${selected ? '#94a3b8' : '#e2e8f0'}`,
        borderRight: `1px solid ${selected ? '#94a3b8' : '#e2e8f0'}`,
        borderBottom: `1px solid ${selected ? '#94a3b8' : '#e2e8f0'}`,
        borderLeft: '6px solid #1e293b',
        borderRadius: 8,
        boxShadow: selected ? '0 4px 12px rgba(0,0,0,0.08)' : '0 2px 6px rgba(0,0,0,0.04)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden'
    };

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [hospitals, setHospitals] = useState<HospitalStatus[]>([]);

    const totalPages = Math.ceil(hospitals.length / itemsPerPage);
    const currentHospitals = hospitals.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const node_id = node.id == "top100ka" ? "top100ka_surgery" : node.id;

    const selectExchange = useAppStore().selectExchange

    useEffect(() => {
        fetchHospitals(node_id, batch)
            .then((data) => {
                setHospitals(data)
            })
            .catch(() => { /* toast handled by interceptor */
            })
    }, [batch]);

    return <BasicCardNode
        node={node}
        data={data}
        cardStyle={cardStyle}
        showTitle={false}
    >
        <div>
            {/*Header标题*/}
            <div style={{fontSize: 12, fontWeight: 600, color: '#1e293b', marginBottom: 8}}>
                Top 20 Underperforming Accounts (MTD)
            </div>
            {/* 表格头部 - 5列网格布局 */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '220px 64px 64px 50px 60px',
                    gap: '4px',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #e2e8f0',
                    marginBottom: '8px',
                }}
            >
                {["Hospital", `Act${selectExchange?.show_char}`, `Gap${selectExchange?.show_char}`, "Achi%", "Vol Gro%"].map((title) => (
                    <div style={{fontSize: 11, fontWeight: 700, color: '#94a3b8', textAlign: 'center'}}>
                        {title}
                    </div>))}
            </div>

            {/* 表格内容 - 数据行 */}
            <div style={{flex: 1, overflow: 'hidden'}}>
                {currentHospitals.map((hospital, i) => (
                    <div
                        key={i}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '220px 64px 64px 50px 60px',
                            gap: '4px',
                            padding: '6px 0',
                            borderBottom: i < currentHospitals.length - 1 ? '1px solid #f1f5f9' : 'none',
                            alignItems: 'center',
                        }}
                    >
                        {/* 医院名称 - 带彩色圆点 */}
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <span
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: hospital.status === 'good' ? '#1e293b' : '#D32F2F',
                                flexShrink: 0,
                            }}
                        />
                            <span
                                style={{
                                    fontSize: 11,
                                    color: '#1e293b',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                            {hospital.name}
                        </span>
                        </div>

                        {/* ACT SALES - 始终黑色 */}
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 500,
                                color: '#1e293b',
                                textAlign: 'center',
                            }}
                        >
                            {formatCompactNumber(hospital.actSales ?? 0,1,'auto',selectExchange,false)}
                        </div>

                        {/* GAP - <0红色，≥0黑色 */}
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: '#1e293b',
                                textAlign: 'center',
                            }}
                        >
                            {(hospital.gap ?? 0) >= 0 ? '+' : ''}{formatCompactNumber(hospital.gap ?? 0,1,'auto',selectExchange,false)}
                        </div>

                        {/* ACHI - 颜色由后端状态字段决定 */}
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: (hospital.status === 'good' || hospital.achi == 0) ? '#1e293b' : '#D32F2F',
                                textAlign: 'center',
                            }}
                        >
                            {formatPercentage(hospital.achi ?? 0, 1)}
                        </div>

                        {/* VOL GR - <0红色，≥0绿色 */}
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: (() => {
                                    const value = hospital.volGr ?? 0
                                    if (value == 0) return '#1e293b'
                                    return value < 0 ? '#D32F2F' : '#059669'
                                })(),
                                textAlign: 'center',
                            }}
                        >
                            {(hospital.volGr ?? 0) > 0 && '+'}
                            {formatPercentage(hospital.volGr ?? 0, 1)}

                        </div>
                    </div>
                ))}
            </div>

            {/* Pagination - 极简风格 */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: '1px solid #f1f5f9',
                }}
            >
                {/* 页码显示 - 左侧 */}
                <span style={{fontSize: 11, color: '#94a3b8', fontWeight: 500}}>
              {currentPage} / {totalPages}
            </span>

                {/* 箭头按钮 - 右侧 */}
                <div style={{display: 'flex', gap: 16}}>
          <span
              style={{
                  fontSize: 16,
                  color: currentPage === 1 ? '#cbd5e1' : '#64748b',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  userSelect: 'none',
                  transition: 'color 0.2s',
              }}
              onClick={(e) => {
                  e.stopPropagation();
                  if (currentPage > 1) setCurrentPage(currentPage - 1);
              }}
          >
            &lt;
          </span>
                    <span
                        style={{
                            fontSize: 16,
                            color: currentPage === totalPages ? '#cbd5e1' : '#64748b',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            userSelect: 'none',
                            transition: 'color 0.2s',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                        }}
                    >
            &gt;
          </span>
                </div>
            </div>
        </div>
    </BasicCardNode>
})
